import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Play, Target, Plus, Trash2, CheckCircle2, Clock } from "lucide-react";

const GOAL_KINDS = ["Emergency Fund", "Vacation", "House", "Wedding", "Retirement", "Education", "Vehicle", "Other"];

export function CashFlowScenario({ window: forecastWindow = 90 }) {
    const [subs, setSubs] = useState([]);
    const [bills, setBills] = useState([]);
    const [skipSubs, setSkipSubs] = useState({});
    const [skipBills, setSkipBills] = useState({});
    const [defer, setDefer] = useState(0);
    const [extraInflow, setExtraInflow] = useState({ amount: 0, date: "" });
    const [extraOutflow, setExtraOutflow] = useState({ amount: 0, date: "" });
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/subscriptions").then((r) => setSubs(r.data.filter((s) => s.active)));
        api.get("/bills").then((r) => setBills(r.data.filter((b) => b.status === "Active"))).catch(() => setBills([]));
    }, []);

    const run = async () => {
        setBusy(true);
        try {
            const body = {
                window: forecastWindow, threshold: 0,
                skip_subscription_ids: Object.keys(skipSubs).filter((k) => skipSubs[k]),
                skip_bill_ids: Object.keys(skipBills).filter((k) => skipBills[k]),
                defer_card_days: Number(defer) || 0,
                extra_income_amount: Number(extraInflow.amount) || 0,
                extra_income_date: extraInflow.date || null,
                extra_outflow_amount: Number(extraOutflow.amount) || 0,
                extra_outflow_date: extraOutflow.date || null,
            };
            const { data } = await api.post("/cash-flow/scenario", body);
            setResult(data);
            toast.success(`Scenario applied · min balance ${fmtMoney(data.min_balance)}`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const savingsPerYear = Object.keys(skipSubs)
        .filter((k) => skipSubs[k])
        .reduce((sum, id) => {
            const s = subs.find((x) => x.id === id);
            if (!s) return sum;
            const mult = { Monthly: 12, Quarterly: 4, "Half-Yearly": 2, Yearly: 1 }[s.frequency || "Monthly"] || 12;
            return sum + (Number(s.amount) || 0) * mult;
        }, 0);

    return (
        <div className="rounded-lg border border-stone-200 bg-white overflow-hidden mb-6" data-testid="cashflow-scenario">
            <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md bg-violet-100 text-violet-700 flex items-center justify-center">
                        <Sparkles size={13} />
                    </div>
                    <div>
                        <div className="eyebrow">What-if simulator</div>
                        <div className="text-[13px] text-stone-700 mt-0.5">Toggle what to skip · re-project balance</div>
                    </div>
                </div>
                {savingsPerYear > 0 && (
                    <div className="text-[12px] px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800">
                        Skipping saves {fmtMoney(savingsPerYear)}/yr
                    </div>
                )}
            </div>

            <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <Label className="eyebrow">Defer card bills</Label>
                        <div className="flex items-center gap-2 mt-1.5">
                            <Input type="number" min={0} max={30} value={defer}
                                   onChange={(e) => setDefer(e.target.value)} className="w-24 font-mono-figure"
                                   data-testid="scenario-defer-days" />
                            <span className="text-xs text-stone-500">days later</span>
                        </div>
                    </div>
                    <div>
                        <Label className="eyebrow">Add hypothetical inflow (₹)</Label>
                        <div className="flex items-center gap-2 mt-1.5">
                            <Input type="number" value={extraInflow.amount}
                                   onChange={(e) => setExtraInflow((s) => ({ ...s, amount: e.target.value }))}
                                   className="font-mono-figure" placeholder="0" />
                            <Input type="date" value={extraInflow.date}
                                   onChange={(e) => setExtraInflow((s) => ({ ...s, date: e.target.value }))} />
                        </div>
                    </div>
                    <div>
                        <Label className="eyebrow">Add planned outflow (₹)</Label>
                        <div className="flex items-center gap-2 mt-1.5">
                            <Input type="number" value={extraOutflow.amount}
                                   onChange={(e) => setExtraOutflow((s) => ({ ...s, amount: e.target.value }))}
                                   className="font-mono-figure" placeholder="0" />
                            <Input type="date" value={extraOutflow.date}
                                   onChange={(e) => setExtraOutflow((s) => ({ ...s, date: e.target.value }))} />
                        </div>
                    </div>
                </div>

                {subs.length > 0 && (
                    <div>
                        <div className="eyebrow mb-1.5">Skip subscription renewals</div>
                        <div className="flex flex-wrap gap-2">
                            {subs.slice(0, 20).map((s) => (
                                <label key={s.id}
                                       className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[12px] cursor-pointer transition-colors ${
                                           skipSubs[s.id] ? "bg-red-50 border-red-200 text-red-700 line-through"
                                                          : "bg-white border-stone-200 hover:border-stone-400"
                                       }`}>
                                    <input type="checkbox" className="hidden"
                                           checked={!!skipSubs[s.id]}
                                           onChange={(e) => setSkipSubs((v) => ({ ...v, [s.id]: e.target.checked }))} />
                                    {s.name} · {fmtMoney(s.amount)}
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {bills.length > 0 && (
                    <div>
                        <div className="eyebrow mb-1.5">Skip recurring bills</div>
                        <div className="flex flex-wrap gap-2">
                            {bills.slice(0, 20).map((b) => (
                                <label key={b.id}
                                       className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[12px] cursor-pointer transition-colors ${
                                           skipBills[b.id] ? "bg-red-50 border-red-200 text-red-700 line-through"
                                                          : "bg-white border-stone-200 hover:border-stone-400"
                                       }`}>
                                    <input type="checkbox" className="hidden"
                                           checked={!!skipBills[b.id]}
                                           onChange={(e) => setSkipBills((v) => ({ ...v, [b.id]: e.target.checked }))} />
                                    {b.operator} · {fmtMoney(b.monthly_rent)}
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between">
                    <Button onClick={run} disabled={busy}
                            className="bg-violet-700 hover:bg-violet-800 text-white" data-testid="scenario-run">
                        <Play size={13} className="mr-1.5" /> {busy ? "Running…" : "Run scenario"}
                    </Button>
                    {result && (
                        <div className="text-[12px] text-stone-600">
                            Min balance: <strong className={result.min_balance < 0 ? "text-red-700" : "text-stone-900"}>
                                {fmtMoney(result.min_balance)}
                            </strong> · Shortfall {result.shortfall_days} day(s)
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const EMPTY_GOAL = { name: "", target_amount: "", target_date: "", monthly_contribution: "", kind: "Emergency Fund", remarks: "" };

export function SavingsGoalsSection() {
    const [rows, setRows] = useState([]);
    const [baseline, setBaseline] = useState(0);
    const [open, setOpen] = useState(false);
    const [f, setF] = useState(EMPTY_GOAL);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/cash-flow/goals");
            setRows(data.goals || []);
            setBaseline(data.current_cash_baseline || 0);
        } catch { setRows([]); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/cash-flow/goals", {
                ...f,
                target_amount: Number(f.target_amount || 0),
                monthly_contribution: Number(f.monthly_contribution || 0),
            });
            toast.success("Savings goal created");
            setOpen(false); setF(EMPTY_GOAL);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this goal?")) return;
        await api.delete(`/cash-flow/goals/${id}`);
        load();
    };

    return (
        <div className="mb-6" data-testid="savings-goals-section">
            <div className="flex items-end justify-between mb-3">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Target size={11} /> Savings goals</div>
                    <p className="text-[13px] text-stone-500 mt-1">Track how each goal lines up against your cash flow trajectory.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <Button size="sm" onClick={() => setOpen(true)} data-testid="goal-add-btn"
                            className="bg-stone-900 hover:bg-stone-800 text-white">
                        <Plus size={13} className="mr-1.5" /> New goal
                    </Button>
                    <DialogContent className="max-w-lg">
                        <DialogHeader><DialogTitle className="font-display text-2xl">New savings goal</DialogTitle></DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                    <Label className="eyebrow">Name</Label>
                                    <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                                           className="mt-1.5" placeholder="Kerala trip 2026" data-testid="goal-name" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Kind</Label>
                                    <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                        <SelectContent>{GOAL_KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="eyebrow">Target amount (₹)</Label>
                                    <Input required type="number" value={f.target_amount}
                                           onChange={(e) => setF({ ...f, target_amount: e.target.value })}
                                           className="mt-1.5 font-mono-figure" data-testid="goal-amount" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Target date</Label>
                                    <Input required type="date" value={f.target_date}
                                           onChange={(e) => setF({ ...f, target_date: e.target.value })} className="mt-1.5" />
                                </div>
                                <div>
                                    <Label className="eyebrow">Monthly contribution (₹)</Label>
                                    <Input type="number" value={f.monthly_contribution}
                                           onChange={(e) => setF({ ...f, monthly_contribution: e.target.value })}
                                           className="mt-1.5 font-mono-figure" />
                                </div>
                                <div className="col-span-2">
                                    <Label className="eyebrow">Remarks</Label>
                                    <Textarea rows={2} value={f.remarks}
                                              onChange={(e) => setF({ ...f, remarks: e.target.value })} className="mt-1.5" />
                                </div>
                            </div>
                            <Button type="submit" disabled={busy}
                                    className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="goal-submit">
                                {busy ? "Creating…" : "Create goal"}
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {rows.length === 0 ? (
                <div className="border border-dashed border-stone-300 rounded-lg p-8 text-center text-stone-500 text-sm">
                    No savings goals yet. Start with an emergency fund of 6× monthly expenses.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {rows.map((g) => (
                        <div key={g.id} className="rounded-lg border border-stone-200 bg-white p-4" data-testid={`goal-card-${g.id}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="eyebrow">{g.kind}</div>
                                    <div className="font-display text-lg mt-1">{g.name}</div>
                                </div>
                                <button onClick={() => remove(g.id)} className="text-stone-400 hover:text-red-600 p-1"><Trash2 size={12} /></button>
                            </div>
                            <div className="mt-3">
                                <div className="flex items-baseline justify-between text-sm">
                                    <span className="text-stone-500">Target</span>
                                    <span className="tabular-nums font-medium">{fmtMoney(g.target_amount)}</span>
                                </div>
                                <div className="mt-2 h-2 bg-stone-100 rounded-full overflow-hidden">
                                    <div className={`h-full ${g.on_track ? "bg-emerald-500" : "bg-amber-500"}`}
                                         style={{ width: `${Math.min(100, g.progress_pct)}%` }} />
                                </div>
                                <div className="flex justify-between text-[11px] text-stone-500 mt-1.5">
                                    <span>{g.progress_pct}% projected</span>
                                    <span>{g.months_left} mo left</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-between mt-3 text-[11px] text-stone-500">
                                <span className="inline-flex items-center gap-1">
                                    <Clock size={10} /> {fmtDate(g.target_date)}
                                </span>
                                {g.on_track ? (
                                    <span className="inline-flex items-center gap-1 text-emerald-700">
                                        <CheckCircle2 size={11} /> On track
                                    </span>
                                ) : (
                                    <span className="text-amber-700">Short of target</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div className="text-[10px] text-stone-400 mt-2">Baseline cash pool (bank snapshots): {fmtMoney(baseline)}</div>
        </div>
    );
}
